import { observer } from "mobx-react-lite";
import {
  ItemListSkeletonRowData,
  ItemListRowType,
  ItemListAsyncCalendarEventRowData,
} from "@/components/item-list/types";
import { FC, useCallback, useRef } from "react";
import { useAppStore } from "@/store";
import { ItemListSkeletonRow } from "../item-list-skeleton-row";
import { ItemListAsyncCalendarEventRowView } from "./item-list-async-calendar-event-row.view";
import { ReactMouseEvent } from "@/domains/react/types";

export interface ItemListAsyncCalendarEventRowProps {
  data: ItemListAsyncCalendarEventRowData;
}

export const ItemListAsyncCalendarEventRow: FC<ItemListAsyncCalendarEventRowProps> = observer(
  ({ data }) => {
    const { store } = useAppStore();
    const calendarEvent = store.spaceAccountCalendarEvents.get(data.payload.calendarEventId);

    const itemRowData =
      calendarEvent && typeof data.payload.itemRow === "function"
        ? data.payload.itemRow(calendarEvent)
        : typeof data.payload.itemRow === "function"
          ? undefined
          : data.payload.itemRow;

    const item = itemRowData?.payload;
    const itemRef = useRef(item);
    itemRef.current = item;

    const handleItemClick = useCallback((event: ReactMouseEvent, canClickItem = true) => {
      event.stopPropagation();
      const item = itemRef.current;
      if (!item) return;
      if (item.listState?.canSelect && event.shiftKey) {
        item.listState.selectItemsFromLastSelectionUntil(item.id);
        return;
      }

      if (canClickItem) {
        item.onClick({ itemId: item.id, event });
      }
    }, []);

    const handleMouseEnter = useCallback(() => {
      const item = itemRef.current;
      if (item) {
        item.listState?.hoverItem(item.id);
      }
    }, []);

    const skeletonRowData: ItemListSkeletonRowData = {
      key: data.key,
      type: ItemListRowType.Skeleton,
      payload: {},
    };

    if (!calendarEvent || !item) {
      return <ItemListSkeletonRow data={skeletonRowData} />;
    }

    const isHovered = item.id === item.listState?.hoveredItemId;
    const isKeyboardHighlighted = item.id === item.listState?.keyboardHighlightedItemId;
    const showActions = isHovered;
    const isHoverable = item.listState?.isHoverable ?? false;
    const isDisabled = item.listState?.isSelectModeActive ?? false;
    const hasAssociatedNote = !!calendarEvent.associatedNote;

    return (
      <ItemListAsyncCalendarEventRowView
        itemRowData={itemRowData}
        startTime={calendarEvent.startTime}
        endTime={calendarEvent.endTime}
        isAllDay={calendarEvent.isAllDay}
        isHighlighted={isKeyboardHighlighted}
        isHovered={isHovered}
        showActions={showActions}
        isHoverable={isHoverable}
        isDisabled={isDisabled}
        hasAssociatedNote={hasAssociatedNote}
        handleItemClick={handleItemClick}
        handleMouseEnter={handleMouseEnter}
      />
    );
  }
);
